import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@src/app/core/interfaces/common/company';
import { Option } from '@src/app/core/interfaces/common/option';
import { FormErrorHandlerService } from '@src/app/core/services/helpers/form-error-handler.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import {
  BenchmarkResponse,
  CreateBenchmarkRequest,
  FloatingInterestRateIndex,
  InvoicingHttpService,
  UpdateBenchmarkRequest,
} from '@src/app/core/services/http/Invoicing/invoicing.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { nameof } from '@src/app/core/utils/nameof.utils';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { first, catchError, of } from 'rxjs';

interface BenchmarkFormData {
  indexer: FloatingInterestRateIndex | null;
  percentage: number | null;
}

@Component({
  selector: 'app-company-benchmark-modal',
  templateUrl: './company-benchmark-modal.component.html',
  styleUrls: ['./company-benchmark-modal.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule, ProgressSpinnerModule],
})
export class CompanyBenchmarkModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @Input() company: Company | null = null;
  @Output() benchmarkUpdated = new EventEmitter<void>();

  benchmarkForm!: FormGroup;
  benchmarkFormProps = nameof<BenchmarkFormData>();

  // Opções de indexador (apenas CDI e PRE)
  indexerOptions: Option[] = [
    { label: 'CDI', value: FloatingInterestRateIndex.CDI },
    { label: 'PRE', value: FloatingInterestRateIndex.PRE },
  ];

  // State management
  override isLoading: boolean = false;
  isEditMode: boolean = false;
  existingBenchmark: BenchmarkResponse | null = null;

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private invoicingService: InvoicingHttpService,
    private formErrorHandler: FormErrorHandlerService,
    private toastHelper: ToastHelperService
  ) {
    super(translateService);
    this.buildForm();
  }

  private buildForm(): void {
    this.benchmarkForm = this.formBuilder.group({
      [this.benchmarkFormProps.indexer]: [null, [Validators.required]],
      [this.benchmarkFormProps.percentage]: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    });
  }

  async open(company: Company) {
    this.isLoading = true;
    this.company = company;
    this.resetForm();

    // Abrir o modal primeiro para mostrar o loading
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'modal-company-benchmark', false, true);

    try {
      // Verificar se a empresa já tem benchmark
      this.invoicingService.getBenchmarkByCompanyId(company.id!).subscribe({
        next: benchmark => {
          if (benchmark) {
            this.isEditMode = true;
            this.existingBenchmark = benchmark;
            this.loadExistingBenchmark(benchmark);
          } else {
            this.isEditMode = false;
            this.existingBenchmark = null;
          }
          this.isLoading = false;
        },
        error: error => {
          console.error('Erro ao carregar benchmark:', error);
          this.formErrorHandler.showToastErrors(error);
          this.isLoading = false;
        },
      });
    } catch (error) {
      console.error('Erro ao abrir modal:', error);
      this.isLoading = false;
    }
  }

  private loadExistingBenchmark(benchmark: BenchmarkResponse) {
    this.benchmarkForm.patchValue({
      [this.benchmarkFormProps.indexer]: benchmark.indexer,
      [this.benchmarkFormProps.percentage]: benchmark.percentage,
    });
  }

  private resetForm(): void {
    this.benchmarkForm.reset();
    this.benchmarkForm.patchValue({
      [this.benchmarkFormProps.indexer]: null,
      [this.benchmarkFormProps.percentage]: null,
    });
  }

  isFormValid(): boolean {
    return this.benchmarkForm.valid;
  }

  submitAction(): void {
    if (!this.isFormValid() || !this.company?.id) {
      this.benchmarkForm.markAllAsTouched();
      return;
    }

    const formValue = this.benchmarkForm.value;
    const indexer = formValue[this.benchmarkFormProps.indexer] as FloatingInterestRateIndex;
    const percentage = formValue[this.benchmarkFormProps.percentage] as number;

    this.isLoading = true;

    if (this.isEditMode && this.existingBenchmark) {
      // Atualizar benchmark existente
      const updateRequest: UpdateBenchmarkRequest = {
        id: this.existingBenchmark.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .updateBenchmark(updateRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoading = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark atualizado com sucesso!');
            this.benchmarkUpdated.emit();
            this.dialogRef?.close();
          }
        });
    } else {
      // Criar novo benchmark
      const createRequest: CreateBenchmarkRequest = {
        companyId: this.company.id,
        indexer: indexer,
        percentage: percentage,
      };

      this.invoicingService
        .createBenchmark(createRequest)
        .pipe(
          first(),
          catchError(error => {
            this.formErrorHandler.showToastErrors(error);
            return of(null);
          })
        )
        .subscribe(result => {
          this.isLoading = false;
          if (result) {
            this.toastHelper.showToastSuccess('Benchmark criado com sucesso!');
            this.benchmarkUpdated.emit();
            this.dialogRef?.close();
          }
        });
    }
  }

  closeModal(): void {
    this.dialogRef?.close();
  }

  getSelectedIndexerLabel(): string {
    const selectedIndexer = this.benchmarkForm.get(this.benchmarkFormProps.indexer)?.value;
    if (!selectedIndexer) return '-';
    const option = this.indexerOptions.find(opt => opt.value === selectedIndexer);
    return option?.label || '-';
  }

  getPercentageValue(): number {
    return this.benchmarkForm.get(this.benchmarkFormProps.percentage)?.value || 0;
  }
}
